import {Button} from 'components/Button';
import {Typography} from 'components/Typography';
import {FC, useCallback} from 'react';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {MODALS} from 'modules/modals/constants';
import {showModal} from 'modules/modals/thunks';
import {ProductBuyCartCounter} from 'modules/product-card/components/ProductBuyCartCounter';
import {useGetProductInCart} from 'hooks/product/useGetProductInCart';
import {selectProductData} from 'modules/product-card/selectors';
import {useIsPrive} from 'core/hooks/useIsPrive';
import {useIsWorker} from 'core/hooks/useIsWorker';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';
import {useReservationFlow} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useReservationFlow';
import {IProductShort} from 'modules/products/types';
import Icon from 'components/Icon';
import {plural} from 'core/helpers';
import style from './style.module.scss';

interface IProps {
    isSticky?: boolean;
}

export const ProductBuyReserveButton: FC<IProps> = ({isSticky = false}) => {
    const dispatch = useAppDispatch();
    const productData = useAppSelector(selectProductData);
    const productInCart = useGetProductInCart();

    const isPrive = useIsPrive();
    const isWorker = useIsWorker();
    const isLoggedIn = useIsLoggedIn();

    const handleNotifyAvailable = useCallback(() => {
        dispatch(showModal(MODALS.PRODUCT_NOTIFY_AVAILABLE.name));
    }, [dispatch]);

    const {reserveButtonText, onReserve, isReservationFlowActive, storesWithStockCount} = useReservationFlow({
        productData: (productData ?? {}) as unknown as IProductShort,
        isPrive,
        isWorker,
        isLoggedIn,
        availability: 'FOR_RESERVATION',
    });

    const winestoresText = plural(storesWithStockCount, ['винотеки', 'винотек', 'винотек']);

    // Приве не может забронировать товар, доступный только в винотеке — показываем
    // тот же паттерн "узнать о наличии", что и на остальных buy-блоках PDP
    if (isPrive) {
        return (
            <Button isStretched={true} onClick={handleNotifyAvailable} size={'large'} theme={'secondary'}>
                <Typography variant={'label-16-r'}>Узнать о наличии</Typography>
            </Button>
        );
    }

    if (productInCart) {
        return <ProductBuyCartCounter />;
    }

    const reserveButton = (
        <Button className={style.reserveButton} isStretched={true} onClick={onReserve} size={'large'} theme={'primary'}>
            <Typography variant={'button-14-m-cl'}>{reserveButtonText}</Typography>
        </Button>
    );

    // В стики-блоке (ProductTopDesktopPanel) блок должен выглядеть так же, как до
    // добавления флоу бронирования — без жёлтой обводки и подписи с числом винотек.
    if (isSticky) {
        return reserveButton;
    }

    return (
        <div className={style.root}>
            {isReservationFlowActive && (
                <>
                    <div className={style.overlayPlaceholder} />
                    <div className={style.winestoreCounter}>
                        <Icon icon={'Express'} className={style.expressIcon} />
                        <Typography variant={'label-12-r'}>
                            Забрать сегодня только из {storesWithStockCount} {winestoresText}
                        </Typography>
                    </div>
                </>
            )}
            {reserveButton}
        </div>
    );
};
